'use client';

import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { motion } from 'framer-motion';
import { Heart, Wine, Users } from 'lucide-react';
import Image from 'next/image';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function AProposPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-card py-20 border-b border-border/40">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            className="font-serif text-5xl md:text-6xl font-bold tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            À Propos de Nous
          </motion.h1>
          <motion.p
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            L&apos;authenticité italienne au cœur de Differdange
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              {...fadeInUp}
              className="text-center mb-16"
            >
              <div className="flex justify-center mb-8">
                <Image
                  src="/images/mockups/projectlogo.png"
                  alt="Crudo e Vino"
                  width={120}
                  height={120}
                  className="h-30 w-30"
                />
              </div>
              <h2 className="font-serif text-4xl font-bold mb-6">
                Notre Histoire
              </h2>
              <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Crudo e Vino est né de la passion pour la cuisine italienne authentique et les vins d&apos;exception.
                  Notre mission est de vous faire découvrir les vraies saveurs de l&apos;Italie, préparées avec soin
                  et des ingrédients de première qualité.
                </p>
                <p>
                  Situé à Differdange, nous sommes fiers d&apos;offrir une expérience culinaire sophistiquée et
                  contemporaine, tout en restant fidèles aux traditions italiennes. Chaque plat est préparé avec amour
                  et respect des recettes ancestrales.
                </p>
                <p>
                  Que vous recherchiez une soirée romantique, un dîner en famille ou un repas d&apos;affaires,
                  Crudo e Vino vous accueille dans une ambiance élégante et chaleureuse.
                </p>
              </div>
            </motion.div>

            {/* Values */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3">
                  Passion
                </h3>
                <p className="text-muted-foreground">
                  Chaque plat est préparé avec amour et dévouement, reflétant notre passion pour la cuisine italienne
                </p>
              </motion.div>

              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.2 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-6">
                  <Wine className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3">
                  Qualité
                </h3>
                <p className="text-muted-foreground">
                  Nous sélectionnons rigoureusement nos ingrédients et nos vins pour garantir une expérience exceptionnelle
                </p>
              </motion.div>

              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.3 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-full bg-foreground/10 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-foreground" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3">
                  Convivialité
                </h3>
                <p className="text-muted-foreground">
                  Notre équipe vous accueille chaleureusement pour partager avec vous l&apos;art de vivre à l&apos;italienne
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Chef's Philosophy */}
      <section className="py-20 bg-card border-t border-border/40">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            {...fadeInUp}
          >
            <h2 className="font-serif text-4xl font-bold mb-6">
              Notre Philosophie
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Chez Crudo e Vino, nous croyons que la meilleure cuisine est celle qui respecte les produits et
                les traditions. Nous travaillons avec des producteurs locaux et italiens pour vous offrir des
                ingrédients frais et de saison.
              </p>
              <p>
                Notre carte des vins, soigneusement élaborée, met en valeur de nombreuses références italiennes,
                des grands crus classiques aux découvertes de petits vignerons passionnés.
              </p>
              <p>
                Consommation sur place, à emporter et en livraison, appelez-nous !
              </p>
              <p className="font-semibold text-foreground">
                Venez découvrir l&apos;Italie dans chaque bouchée, chaque gorgée, chaque moment passé chez nous.
              </p>
            </div>

            <div className="mt-12">
              <a
                href="/reserver"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary-foreground bg-primary rounded-lg hover:opacity-90 transition-all hover:scale-105 shadow-lg"
              >
                Réserver une Table
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
